import styles from './AppointmentHeader.module.css';
import { useInstitutionsStore } from '@/stores/institutions';
import { IAppointmentButtonColors, TScheduleType } from '@/types';

type TProps = {
  variant: TScheduleType;
  text: string;
};

/**
 * Renders a customizable appointment header with optional visual effects.
 * The colors and visual styles depend on the current institution's whitelabel settings.
 * @param props - Component props.
 * @param props.variant - The type of header ('appointment' or 'ondemand').
 * @param props.text - The text displayed in the header.
 * @returns A React element representing the appointment header.
 */
export const AppointmentHeader = ({ variant, text }: TProps) => {
  const { currentInstitution } = useInstitutionsStore((state) => ({
    currentInstitution: state.currentInstitution,
  }));

  const colors: Record<
    TScheduleType,
    IAppointmentButtonColors & { isSolid: boolean }
  > = {
    scheduled: {
      text:
        currentInstitution?.whitelabel?.enabled &&
        currentInstitution?.whitelabel?.scheduleTextColor
          ? currentInstitution.whitelabel.scheduleTextColor
          : '#FFFFFF',
      background:
        currentInstitution?.whitelabel?.enabled &&
        currentInstitution?.whitelabel?.scheduleBackgroundColor
          ? currentInstitution.whitelabel.scheduleBackgroundColor
          : '#000034',
      highlight1:
        currentInstitution?.whitelabel?.enabled &&
        currentInstitution?.whitelabel?.scheduleHighlightOneColor
          ? currentInstitution.whitelabel.scheduleHighlightOneColor
          : '#0f479a',
      highlight2:
        currentInstitution?.whitelabel?.enabled &&
        currentInstitution?.whitelabel?.scheduleHighlightTwoColor
          ? currentInstitution.whitelabel.scheduleHighlightTwoColor
          : '#3476d8',
      isSolid: currentInstitution?.whitelabel?.enabled
        ? (currentInstitution?.whitelabel?.isSolidSchedule ?? false)
        : false,
    },
    on_demand: {
      text:
        currentInstitution?.whitelabel?.enabled &&
        currentInstitution?.whitelabel?.onDemandTextColor
          ? currentInstitution.whitelabel.onDemandTextColor
          : '#FFFFFF',
      background:
        currentInstitution?.whitelabel?.enabled &&
        currentInstitution?.whitelabel?.onDemandBackgroundColor
          ? currentInstitution.whitelabel.onDemandBackgroundColor
          : '#000034',
      highlight1:
        currentInstitution?.whitelabel?.enabled &&
        currentInstitution?.whitelabel?.onDemandHighlightOneColor
          ? currentInstitution.whitelabel.onDemandHighlightOneColor
          : '#F47E52',
      highlight2:
        currentInstitution?.whitelabel?.enabled &&
        currentInstitution?.whitelabel?.onDemandHighlightTwoColor
          ? currentInstitution.whitelabel.onDemandHighlightTwoColor
          : '#A92E00',
      isSolid: currentInstitution?.whitelabel?.enabled
        ? (currentInstitution?.whitelabel?.isSolidOnDemand ?? false)
        : false,
    },
  };

  return (
    <div
      data-testid="appointment-header"
      className={styles.headerContainer}
      style={{ background: colors[variant].background }}
    >
      {!colors[variant].isSolid && (
        <>
          <div className={styles.shineBackground}></div>
          <div
            className={`${styles.blob} ${styles.blob2}`}
            style={{ background: colors[variant].highlight1 }}
          ></div>
          <div
            className={`${styles.blob} ${styles.blob1}`}
            style={{ background: colors[variant].highlight2 }}
          ></div>
          <div className={styles.blob3}></div>
          <div className={styles.group}></div>
          <div className={styles.noise}></div>
        </>
      )}
      {!colors[variant].isSolid && <div className={styles.ellipse}></div>}
      <div className={styles.text} style={{ color: colors[variant].text }}>
        {text}
      </div>
    </div>
  );
};
